import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  BookOpen, 
  Sparkles, 
  User, 
  CheckCircle2, 
  ShieldCheck, 
  Globe, 
  ArrowRight, 
  ExternalLink 
} from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { t } from '../translations';

// Verklig fackgranskad artikel med verifierad och fungerande DOI till Nature
const REAL_SAMPLE_ARTICLE = {
  venue: "Nature",
  date: "10 maj 2023",
  url: "https://doi.org/10.1038/s41586-023-06063-y",
  authors: "L. A. Rojas, Z. Sethna, K. C. Soares et al. · Memorial Sloan Kettering",
  topic: {
    sv: "Immunologi & Onkologi",
    en: "Immunology & Oncology"
  },
  title: {
    sv: "Individanpassat mRNA-vaccin framkallar hållbart immunsvar mot bukspottkörtelcancer",
    en: "Personalized RNA neoantigen vaccines stimulate T cells in pancreatic cancer"
  },
  content: {
    sv: {
      summaryHeading: "🎯 Sammanfattning",
      summaryText: "Forskare har i en klinisk fas I-studie utvärderat ett skräddarsytt mRNA-vaccin mot bukspottkörtelcancer. Genom att sekvensera varje patients specifika tumör skapades individanpassade vacciner riktade mot upp till 20 unika mutationer (neoantigener). Behandlingen kombinerades med immunterapi och standardkirurgi för att träna immunförsvarets T-celler att eliminera mikroskopiska tumörrester.",
      
      contextHeading: "🌏 Större Sammanhang",
      contextText: "Bukspottkörtelcancer är en av de dödligaste tumörformerna och har historiskt betraktats som immunologiskt 'kall' – det vill säga okänslig för modern immunterapi. Denna studie visar att individanpassade mRNA-vacciner kan väcka ett målinriktat immunsvar även vid tumörer med låg mutationsbörda, vilket öppnar en helt ny väg för adjuvant precisionsonkologi.",
      
      resultsHeading: "🔑 Nyckelresultat",
      results: [
        {
          bold: "Hög svarsfrekvens och långvarigt immunsvar",
          text: "8 av 16 patienter (50 %) utvecklade mätbara och kraftiga T-cellssvar specifika för tumörens neoantigener, vilka kvarstod upp till två år efter behandlingen."
        },
        {
          bold: "Signifikant fördröjt återfall",
          text: "Patienter med verifierat T-cellsvar uppvisade signifikant längre återfallsfri överlevnad jämfört med icke-svarare under studiens uppföljningsperiod."
        }
      ]
    },
    en: {
      summaryHeading: "🎯 Summary",
      summaryText: "In a phase I clinical trial, researchers evaluated a personalized mRNA vaccine for pancreatic cancer. By sequencing individual patient tumors, tailored vaccines targeting up to 20 unique neoantigens were synthesized. The regimen was administered alongside immunotherapy and surgery to prime host T cells against residual microscopic disease.",
      
      contextHeading: "🌏 Broader Context",
      contextText: "Pancreatic ductal adenocarcinoma is typically immunologically 'cold' and unresponsive to conventional immune checkpoint blockade. This study provides proof-of-concept that personalized neoantigen mRNA vaccines can provoke clinically durable immunity even in low-mutation tumors, establishing a new therapeutic paradigm in adjuvant oncology.",
      
      resultsHeading: "🔑 Key Results",
      results: [
        {
          bold: "Robust and durable immunogenicity",
          text: "8 out of 16 patients (50%) mounted neoantigen-specific T-cell responses that persisted for up to two years post-vaccination."
        },
        {
          bold: "Prolonged recurrence-free survival",
          text: "Patients demonstrating vaccine-induced T-cell activity experienced significantly longer recurrence-free survival compared to non-responders."
        }
      ]
    }
  }
};

const LoginView = ({ handleGoogleLogin, handleAppleLogin }) => {
  const { language, setLanguage, languages } = useLanguage();
  
  // Alltid svenska som default i kortet om inte språket i gränssnittet är satt till engelska
  const [cardLang, setCardLang] = useState(language === 'English' || language === 'en' ? 'en' : 'sv');
  const activeContent = REAL_SAMPLE_ARTICLE.content[cardLang];

  const scrollToAuth = () => {
    const authElement = document.getElementById('auth-card');
    if (authElement) {
      authElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const ui = {
    badgeTop: t(language, 'login_badge_verified'),
    cardTitle: t(language, 'login_card_title'),
    cardSubtitle: t(language, 'login_card_subtitle'),
    loginGoogle: t(language, 'login_google'),
    loginApple: t(language, 'login_apple'),
    termsPrefix: t(language, 'login_terms_prefix'),
    termsLink: t(language, 'login_terms_link'),
    termsAnd: t(language, 'login_terms_and'),
    privacyLink: t(language, 'login_privacy_link'),
    footerCopy: t(language, 'login_footer_copy'),
    footerAbout: t(language, 'about_title'),
    footerTerms: t(language, 'login_footer_terms'),
    footerPrivacy: t(language, 'login_footer_privacy')
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] text-slate-900 font-sans flex flex-col justify-between">
      
      {/* Top Navbar */}
      <header className="w-full max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center text-white shadow-md">
            <BookOpen size={20} />
          </div>
          <span className="font-serif italic font-bold text-xl text-slate-900 tracking-tight">
            Science Monitor
          </span>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/about"
            className="text-xs font-semibold text-slate-600 hover:text-slate-900 bg-white border border-slate-200 rounded-full px-3.5 py-1.5 shadow-xs hover:border-slate-300 transition-colors cursor-pointer"
          >
            {ui.footerAbout}
          </Link>

          <div className="relative inline-flex items-center bg-white border border-slate-200 rounded-full px-2.5 py-1 shadow-xs hover:border-slate-300 transition-colors">
            <Globe size={14} className="text-slate-500 mr-1.5" />
            <select
              value={language}
              onChange={(e) => {
                setLanguage(e.target.value);
                setCardLang(e.target.value === 'English' || e.target.value === 'en' ? 'en' : 'sv');
              }}
              className="bg-transparent text-xs font-semibold text-slate-700 focus:outline-hidden cursor-pointer"
            >
              {languages.map((lang) => (
                <option key={lang} value={lang}>{lang}</option>
              ))}
            </select>
          </div>

          <div className="hidden sm:flex items-center gap-2 text-xs font-semibold text-slate-600 bg-white px-3.5 py-1.5 rounded-full border border-slate-200 shadow-xs">
            <ShieldCheck size={14} className="text-emerald-600" />
            <span>{ui.badgeTop}</span>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="w-full max-w-6xl mx-auto px-6 pt-6 pb-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center">
          
          {/* VÄNSTER: Rubrik & primär CTA */}
          <div className="lg:col-span-5 text-left space-y-6">
            <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-xs font-semibold tracking-wide">
              <Sparkles size={14} className="text-blue-500" />
              <span>Forskningsöversikt på 30 sekunder</span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-slate-900 tracking-tight leading-[1.15]">
              Världens forskning. <br />
              <span className="italic text-slate-700">Begriplig varje morgon.</span>
            </h1>

            <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
              Vi scannar öppna vetenskapliga arkiv och sammanfattar nya rön på ren svenska – anpassat efter din nivå och dina intressen.
            </p>

            <div className="pt-2 space-y-3">
              <button
                onClick={scrollToAuth}
                className="inline-flex items-center justify-center gap-2.5 w-full sm:w-auto px-6 py-3.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-medium text-sm sm:text-base shadow-sm transition-all hover:translate-y-[-1px] active:scale-[0.99] cursor-pointer"
              >
                <span>Få sammanfattningar som denna varje morgon</span>
                <ArrowRight size={18} />
              </button>
              
              <div className="flex items-center gap-4 text-xs text-slate-500 font-sans">
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 size={14} className="text-emerald-500" /> Gratis att prova
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 size={14} className="text-emerald-500" /> Inget betalkort krävs
                </span>
              </div>
            </div>
          </div>

          {/* HÖGER: Riktigt produktkort med exakta sektioner från Inbox */}
          <div className="lg:col-span-7">
            <div className="relative mx-auto w-full max-w-xl">
              <div className="absolute -inset-1.5 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-3xl blur-xl opacity-60 -z-10" />

              <div className="bg-white rounded-3xl border border-slate-200/90 shadow-md shadow-slate-100 overflow-hidden text-left flex flex-col">
                
                {/* Header-sektion */}
                <div className="p-6 sm:p-8 border-b border-slate-100 shrink-0">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-[10px] font-bold text-blue-700 uppercase tracking-widest bg-blue-50 border border-blue-100/60 px-3 py-1 rounded-full">
                      {REAL_SAMPLE_ARTICLE.topic[cardLang]}
                    </span>

                    {/* Quick-toggle för språk */}
                    <div className="flex items-center bg-slate-100 p-0.5 rounded-lg border border-slate-200/80">
                      <button
                        type="button"
                        onClick={() => setCardLang('sv')}
                        className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all cursor-pointer ${
                          cardLang === 'sv'
                            ? 'bg-white text-slate-900 shadow-xs'
                            : 'text-slate-500 hover:text-slate-800'
                        }`}
                      >
                        🇸🇪 SV
                      </button>
                      <button
                        type="button"
                        onClick={() => setCardLang('en')}
                        className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all cursor-pointer ${
                          cardLang === 'en'
                            ? 'bg-white text-slate-900 shadow-xs'
                            : 'text-slate-500 hover:text-slate-800'
                        }`}
                      >
                        🇬🇧 EN
                      </button>
                    </div>
                  </div>

                  <h3 className="text-xl sm:text-2xl font-serif mb-4 leading-snug text-slate-900">
                    {REAL_SAMPLE_ARTICLE.title[cardLang]}
                  </h3>

                  <div className="space-y-1.5 pt-1">
                    <p className="text-xs sm:text-sm text-slate-600 font-medium italic flex items-start gap-2">
                      <User size={15} className="shrink-0 mt-0.5 text-slate-400" />
                      <span className="leading-relaxed">{REAL_SAMPLE_ARTICLE.authors}</span>
                    </p>
                    <p className="text-[11px] sm:text-xs text-slate-400 uppercase tracking-wider font-sans">
                      {REAL_SAMPLE_ARTICLE.venue} • {REAL_SAMPLE_ARTICLE.date}
                    </p>
                  </div>
                </div>

                {/* Sammanfattning med exakta rubriker och format från Inbox */}
                <div className="p-6 sm:p-8 bg-slate-50/50 flex-1 flex flex-col justify-between space-y-6">
                  <div>
                    {/* Exakt AI SUMMARY badge som i inkorgen */}
                    <div className="flex items-center gap-1.5 text-[11px] font-bold text-indigo-600 uppercase tracking-widest mb-6">
                      <Sparkles size={14} /> 
                      <span>AI SUMMARY</span>
                    </div>

                    <div className="space-y-6 text-slate-700 text-sm sm:text-base font-serif">
                      
                      {/* Sektion 1: Sammanfattning */}
                      <div className="space-y-2">
                        <h4 className="font-sans font-bold text-base text-slate-900 flex items-center gap-2">
                          {activeContent.summaryHeading}
                        </h4>
                        <p className="leading-relaxed text-slate-700">
                          {activeContent.summaryText}
                        </p>
                      </div>

                      {/* Sektion 2: Större Sammanhang */}
                      <div className="space-y-2">
                        <h4 className="font-sans font-bold text-base text-slate-900 flex items-center gap-2">
                          {activeContent.contextHeading}
                        </h4>
                        <p className="leading-relaxed text-slate-700">
                          {activeContent.contextText}
                        </p>
                      </div>

                      {/* Sektion 3: Nyckelresultat med punktlista */}
                      <div className="space-y-3">
                        <h4 className="font-sans font-bold text-base text-slate-900 flex items-center gap-2">
                          {activeContent.resultsHeading}
                        </h4>
                        <ul className="space-y-2.5">
                          {activeContent.results.map((res, idx) => (
                            <li key={idx} className="space-y-0.5">
                              <p className="leading-relaxed text-slate-800">
                                <span className="font-sans font-bold text-slate-900">
                                  • {res.bold}:
                                </span>{" "}
                                {res.text}
                              </p>
                            </li>
                          ))}
                        </ul>
                      </div>

                    </div>
                  </div>

                  {/* Fot med originalstudie som leder direkt till Nature */}
                  <div className="pt-5 border-t border-slate-200/70 flex justify-between items-center shrink-0">
                    <a
                      href={REAL_SAMPLE_ARTICLE.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1.5 cursor-pointer transition-colors"
                    >
                      <span>{cardLang === 'sv' ? 'Läs originalstudie på Nature' : 'Read original paper on Nature'}</span>
                      <ExternalLink size={13} />
                    </a>
                    <span className="text-[11px] text-slate-400 font-sans tracking-wide">
                      {cardLang === 'sv' ? 'Fackgranskad forskning' : 'Peer-reviewed'}
                    </span>
                  </div>
                </div>

              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Auth-sektion under fold */}
      <section id="auth-card" className="w-full max-w-md mx-auto px-6 py-12 md:py-16">
        <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/60 p-8 sm:p-10 border border-slate-200">
          
          <div className="text-center mb-6">
            <h2 className="text-2xl font-serif font-bold text-slate-900">
              {ui.cardTitle}
            </h2>
            <p className="text-slate-500 text-xs sm:text-sm mt-1">
              {ui.cardSubtitle}
            </p>
          </div>

          <div className="space-y-3.5">
            <button 
              onClick={handleGoogleLogin} 
              className="w-full flex items-center justify-center gap-3 bg-white border border-slate-300 hover:border-slate-400 py-3.5 px-4 rounded-2xl font-bold text-slate-700 hover:bg-slate-50 transition-all shadow-xs active:scale-95 cursor-pointer text-sm"
            >
              <img src="https://www.google.com/favicon.ico" alt="Google" className="w-4 h-4" />
              <span>{ui.loginGoogle}</span>
            </button>

            <button 
              onClick={handleAppleLogin} 
              className="w-full flex items-center justify-center gap-3 bg-black hover:bg-slate-900 py-3.5 px-4 rounded-2xl font-bold text-white transition-all shadow-xs active:scale-95 cursor-pointer text-sm"
            >
              <svg className="w-4 h-4" viewBox="0 0 384 512" fill="currentColor">
                <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
              </svg>
              <span>{ui.loginApple}</span>
            </button>
          </div>

          <div className="mt-6 pt-6 border-t border-slate-100 space-y-2.5">
            <div className="flex items-center gap-2 text-xs text-slate-600 font-medium">
              <CheckCircle2 size={15} className="text-emerald-500 shrink-0" />
              <span>14 dagars fri provperiod</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-600 font-medium">
              <CheckCircle2 size={15} className="text-emerald-500 shrink-0" />
              <span>Inget betalkort krävs</span>
            </div>
          </div>

          <div className="pt-6 mt-6 border-t border-slate-100 text-center">
            <p className="text-[11px] text-slate-400 leading-relaxed">
              {ui.termsPrefix}
              <Link 
                to="/terms" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-slate-600 hover:text-indigo-600 underline transition-colors"
              >
                {ui.termsLink}
              </Link>
              {ui.termsAnd}
              <Link 
                to="/privacy" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-slate-600 hover:text-indigo-600 underline transition-colors"
              >
                {ui.privacyLink}
              </Link>.
            </p>
          </div>

        </div>
      </section>

      {/* Footer */}
      <footer className="w-full max-w-6xl mx-auto px-6 py-6 border-t border-slate-200/60 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-400 gap-4">
        <p>© {new Date().getFullYear()} {ui.footerCopy}</p>
        <div className="flex items-center gap-6">
          <Link to="/about" className="hover:text-slate-600 transition-colors font-medium">
            {ui.footerAbout}
          </Link>
          <Link to="/terms" className="hover:text-slate-600 transition-colors">
            {ui.footerTerms}
          </Link>
          <Link to="/privacy" className="hover:text-slate-600 transition-colors">
            {ui.footerPrivacy}
          </Link>
        </div>
      </footer>

    </div>
  );
};

export default LoginView;
